import React, { useState, useEffect } from 'react';
import { User } from '../types';
import { authFetch } from '../utils/api';
import { buildApiUrl } from '../config/api';

interface UserListProps {
  onEdit: (user: User) => void;
  onSelect?: (user: User) => void;
  selectedUser?: User | null;
  refreshTrigger: number;
  onNarrative: (message: string, type: 'info' | 'success' | 'error') => void;
}

export const UserList: React.FC<UserListProps> = ({ onEdit, onSelect, selectedUser, refreshTrigger, onNarrative }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);

  const API_URL = buildApiUrl('/users');

  const loadUsers = async () => {
    onNarrative('📡 Summoning users from the edge network...', 'info');
    setLoading(true);
    
    try {
      const response = await fetch(API_URL);
      if (!response.ok) throw new Error('Failed to fetch users');
      
      const data = await response.json();
      setUsers(data);
      onNarrative(`✅ Located ${data.length} users in the matrix!`, 'success');
    } catch (error) {
      onNarrative('💀 DATABASE CONNECTION LOST!', 'error');
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [refreshTrigger]);

  const handleDelete = async (user: User) => {
    if (!confirm(`⚠️ CRITICAL DECISION POINT!\n\nDelete user "${user.name}"?\n\nThis action CANNOT be undone!`)) {
      onNarrative('🛡️ Deletion ABORTED. Crisis averted.', 'info');
      return;
    }

    onNarrative(`🗑️ INITIATING DELETION SEQUENCE for user #${user.id}...`, 'info');
    
    try {
      const response = await authFetch(`${API_URL}/${user.id}`, {
        method: 'DELETE',
      });

      if (!response.ok) throw new Error('Failed to delete user');
      
      onNarrative(`💥 USER #${user.id} VAPORIZED! They're gone... forever.`, 'success');
      await loadUsers();
    } catch (error) {
      onNarrative('❌ DELETION FAILED! The user lives on!', 'error');
      console.error('Error deleting user:', error);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-pulse text-2xl">
          🔄 QUANTUM DATABASE SYNCHRONIZATION IN PROGRESS...
        </div>
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p className="text-xl">📭 The database echoes with emptiness...</p>
        <p className="mt-2">Create the first user to begin the saga!</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full bg-white border border-gray-300">
        <thead className="bg-gray-900 text-white">
          <tr>
            <th className="px-6 py-3 text-left">ID</th>
            <th className="px-6 py-3 text-left">Name</th>
            <th className="px-6 py-3 text-left">Email</th>
            <th className="px-6 py-3 text-left">Created</th>
            <th className="px-6 py-3 text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user, index) => (
            <tr 
              key={user.id} 
              className={`${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-blue-50 transition-colors ${
                selectedUser?.id === user.id ? 'ring-2 ring-blue-500' : ''
              } cursor-pointer`}
              onClick={() => onSelect && onSelect(user)}
            >
              <td className="px-6 py-4 font-mono">#{user.id}</td>
              <td className="px-6 py-4 font-semibold">{user.name}</td>
              <td className="px-6 py-4">{user.email || <span className="text-gray-400">N/A</span>}</td>
              <td className="px-6 py-4 text-sm">
                {new Date(user.created_at).toLocaleString()}
              </td>
              <td className="px-6 py-4 text-center">
                <button
                  onClick={() => onEdit(user)}
                  className="bg-blue-500 text-white px-3 py-1 rounded mr-2 hover:bg-blue-600 transition-colors"
                >
                  ✏️ EDIT
                </button>
                <button
                  onClick={() => handleDelete(user)}
                  className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 transition-colors"
                >
                  🗑️ DELETE
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-4 text-center text-sm text-gray-600">
        📊 Total users in the quantum database: {users.length}
      </div>
    </div>
  );
};